import { getISOWeek } from 'date-fns';

/**
 * Orden canónico de las semanas dentro de una temporada: 27→53 y luego 1→26.
 *
 * Vive en su propio módulo, sin imports internos, para que lo puedan usar
 * tanto `seasonWeeks` como `utils/functions` sin generar ciclos de importación.
 */
export const DEFAULT_SEASON_WEEKS = [
  27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45,
  46, 47, 48, 49, 50, 51, 52, 53, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14,
  15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26,
];

const SEASON_WEEKS_WITHOUT_53 = DEFAULT_SEASON_WEEKS.filter((w) => w !== 53);

/**
 * La temporada ("2026-2027") tiene semana 53: su año de inicio tiene 53
 * semanas ISO (el 28 de diciembre cae en la última semana).
 */
export const seasonHasWeek53 = (season?: string | null): boolean => {
  const startYear = season ? parseInt(season, 10) : NaN;
  return !!startYear && getISOWeek(new Date(startYear, 11, 28)) === 53;
};

/**
 * Semanas en orden de temporada (27→26). La 53 va solo si alguna de las
 * temporadas la tiene.
 */
export const getSeasonWeeks = (
  seasons: (string | null | undefined)[]
): number[] =>
  seasons.some(seasonHasWeek53) ? DEFAULT_SEASON_WEEKS : SEASON_WEEKS_WITHOUT_53;

const SEASON_WEEK_POSITIONS = new Map(
  DEFAULT_SEASON_WEEKS.map((week, index) => [week, index])
);

/** Posición de la semana dentro de la temporada. Las desconocidas van al final. */
export const getSeasonWeekPosition = (week: number): number =>
  SEASON_WEEK_POSITIONS.get(week) ?? Number.MAX_SAFE_INTEGER;

/** Extrae el número de una clave tipo `"W8"`. */
export const parseWeekKey = (key: string | number): number =>
  typeof key === 'number' ? key : parseInt(String(key).replace('W', ''), 10);

/** Comparador de semanas en orden de temporada (27→53, 1→26). */
export const compareSeasonWeeks = (a: number, b: number): number =>
  getSeasonWeekPosition(a) - getSeasonWeekPosition(b);

/** Comparador de claves tipo `"W8"` en orden de temporada. */
export const compareSeasonWeekKeys = (
  a: string | number,
  b: string | number
): number => compareSeasonWeeks(parseWeekKey(a), parseWeekKey(b));

/**
 * Devuelve las semanas de temporada que van desde la primera hasta la última
 * de `weeks`, incluyendo las intermedias que no aparezcan. Sirve para que los
 * saltos de semanas sin datos se vean como huecos en el eje y no colapsados.
 * La 53 se incluye si viene en `weeks` o si alguna de `seasons` la tiene.
 */
export const fillSeasonWeekGaps = (
  weeks: number[],
  seasons: (string | null | undefined)[] = []
): number[] => {
  const seasonWeeks = weeks.includes(53)
    ? DEFAULT_SEASON_WEEKS
    : getSeasonWeeks(seasons);
  const positions = weeks
    .map((week) => seasonWeeks.indexOf(week))
    .filter((position) => position >= 0);

  if (positions.length === 0) return [];

  return seasonWeeks.slice(
    Math.min(...positions),
    Math.max(...positions) + 1
  );
};
